import { LitElement, html, unsafeCSS } from "lit"
import { customElement, state } from "lit/decorators.js"
import { MiniRouter } from "../router"
import "./fits-viewer"
import "./celestial-tracker"
import "./logo-display"
import "./wordmark"
import "./help-viewer"
import styles from "./styles/app-shell.css?inline"
import type { CelestialTracker } from "./celestial-tracker"

@customElement("app-shell")
export class AppShell extends LitElement {
  @state() private currentPath = this.normalizePath(window.location.pathname)
  @state() private adsenseScriptLoaded = false
  @state() private adsenseInitialized = false
  private router = new MiniRouter({
    onChange: path => {
      this.currentPath = this.normalizePath(path)
    },
  })

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.router.dispose()
  }

  /**
   * Dynamically loads the AdSense script only when needed
   * This prevents "ads without content" warnings
   */
  private loadAdSenseScript(): Promise<void> {
    // Already loaded or loading
    if (
      this.adsenseScriptLoaded ||
      document.querySelector('script[src*="adsbygoogle.js"]')
    ) {
      this.adsenseScriptLoaded = true
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.async = true
      script.src =
        "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3543422567904673"
      script.crossOrigin = "anonymous"

      script.onload = () => {
        this.adsenseScriptLoaded = true
        console.log("AdSense script loaded successfully")
        resolve()
      }

      script.onerror = () => {
        console.error("Failed to load AdSense script")
        reject(new Error("AdSense script load failed"))
      }

      document.head.appendChild(script)
    })
  }

  /**
   * Initializes ads after script is loaded and content exists
   */
  private async initializeAds(): Promise<void> {
    if (this.adsenseInitialized) return

    try {
      await this.loadAdSenseScript()

      // Small delay to ensure ad elements are in DOM
      await new Promise(resolve => setTimeout(resolve, 100))

      const adElements = document.querySelectorAll(".adsbygoogle")
      adElements.forEach((adElement: Element) => {
        // Only initialize if not already processed
        if (!(adElement as any).dataset.adsbygoogleStatus) {
          try {
            ;((window as any).adsbygoogle =
              (window as any).adsbygoogle || []).push({})
            this.adsenseInitialized = true
            console.log("AdSense ad initialized")
          } catch (e) {
            console.error("AdSense initialization error:", e)
          }
        }
      })
    } catch (error) {
      console.error("Failed to initialize ads:", error)
    }
  }

  /**
   * Lazy-load ads only when content exists
   * This prevents "ads without publisher-content" warnings
   */
  updated(changedProperties: Map<string, any>): void {
    super.updated(changedProperties)

    // Check if we're on tracker route and content exists
    if (this.currentPath === "/tracker" && !this.adsenseInitialized) {
      // Query the celestial-tracker element
      const tracker = this.shadowRoot?.querySelector(
        "celestial-tracker"
      ) as CelestialTracker | null

      // Only initialize ads if content has been loaded
      if (tracker?.hasContent) {
        console.log("Content detected, lazy-loading AdSense...")
        this.initializeAds()
      }
    }
  }

  static styles = unsafeCSS(styles)

  protected render() {
    const path = this.currentPath
    const view = this.renderRoute(path)
    const showHeader = path !== "/start"

    return html`
      ${showHeader
        ? html`
            <header>
              <div class="header-inner">
                <a class="brand" href="/start" @click=${this.handleLink}>
                  <app-wordmark .showTrademark=${true}></app-wordmark>
                </a>
                <nav>
                  <a
                    class=${`nav ${path === "/fits" ? "active" : ""}`}
                    href="/fits"
                    @click=${this.handleLink}
                    >FITS</a
                  >
                  <a
                    class=${`nav ${path === "/tracker" ? "active" : ""}`}
                    href="/tracker"
                    @click=${this.handleLink}
                    >Celestial Tracker</a
                  >
                  <a
                    class=${`nav ${path.startsWith("/help") ? "active" : ""}`}
                    href="/help"
                    @click=${this.handleLink}
                    >Help</a
                  >
                </nav>
              </div>
            </header>
          `
        : null}
      <main>
        <div class="content">${view}</div>
      </main>
    `
  }

  private handleLink = (event: Event) => {
    event.preventDefault()
    const anchor = event.currentTarget as HTMLAnchorElement
    const href = anchor.getAttribute("href")
    if (!href) return
    this.router.navigate(href)
    this.currentPath = this.normalizePath(href)
  }

  private renderRoute(path: string) {
    // Handle help routes - support nested paths like /help/slooh/capturing-live
    if (path.startsWith("/help")) {
      // Extract everything after /help/ to support nested directories
      const pathParts = path.split("/").slice(2) // Remove empty string and "help"
      const page = pathParts.length > 0 ? pathParts.join("/") : "home"
      return html`<help-viewer
        .page=${page}
        .router=${this.router}
      ></help-viewer>`
    }

    switch (path) {
      case "/tracker":
        return html`<celestial-tracker>
          <ins
            slot="ad-slot"
            class="adsbygoogle"
            style="display:block"
            data-ad-format="fluid"
            data-ad-layout-key="-7f+f1-1r-45+e9"
            data-ad-client="ca-pub-3543422567904673"
            data-ad-slot="9065914683"
          ></ins>
        </celestial-tracker>`
      case "/fits":
        return html`<fits-viewer></fits-viewer>`
      case "/start":
        return html`<start-page
          .onStart=${this.handleStartClick}
          .onLearnMore=${this.handleLearnMoreClick}
        ></start-page>`
      default:
        return html`<start-page
          .onStart=${this.handleStartClick}
          .onLearnMore=${this.handleLearnMoreClick}
        ></start-page>`
    }
  }

  private normalizePath(path: string) {
    if (!path || path === "/") return "/start"
    const trimmed = path.replace(/\/+$/, "")
    return trimmed === "" ? "/start" : trimmed
  }

  private handleStartClick = () => {
    this.router.navigate("/tracker")
  }

  private handleLearnMoreClick = () => {
    this.router.navigate("/help")
  }
}
